import type { User } from '@/types';
import { isPlatformSuperAdmin, parseIsSuperAdminFlag } from '@/utils/authUser';

export interface JwtClaims {
  permissions?: string[];
  roles?: string[];
  is_super_admin?: boolean | number | string;
  /** Present on access tokens for non–super-admin users; list/import APIs use this scope. */
  company_id?: string | null;
}

/**
 * Decode JWT payload (middle segment) without verification — same claims the API signed.
 */
export function decodeJwtPayload(token: string): JwtClaims | null {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const json = atob(padded);
    return JSON.parse(json) as JwtClaims;
  } catch {
    return null;
  }
}

/**
 * Attach permissions, roles, and platform super-admin flag from the access token onto the user object for UI filtering.
 */
export function mergeUserClaimsFromToken(user: User, token: string | null): User {
  if (!token) return user;
  const payload = decodeJwtPayload(token);
  if (!payload) return user;
  const roles = Array.isArray(payload.roles) ? payload.roles : user.roles ?? [];
  const mergedForSuperCheck = {
    is_super_admin: parseIsSuperAdminFlag(payload.is_super_admin)
      ? true
      : user.is_super_admin,
    roles,
  };
  const isSuperAdminFlag = isPlatformSuperAdmin(mergedForSuperCheck);
  const companyFromToken =
    typeof payload.company_id === 'string' && payload.company_id.trim() !== ''
      ? payload.company_id.trim()
      : undefined;
  return {
    ...user,
    permissions: Array.isArray(payload.permissions)
      ? payload.permissions
      : user.permissions ?? [],
    roles,
    is_super_admin: isSuperAdminFlag,
    company_id: companyFromToken ?? user.company_id,
  };
}

/** Company scope for manufacturing/finance queries: persisted user first, then JWT (matches server import/list). */
export function getCompanyIdFromStorage(): string {
  const userStr = localStorage.getItem('user');
  if (userStr) {
    try {
      const u = JSON.parse(userStr) as User;
      const id = u.company_id;
      if (typeof id === 'string' && id.trim() !== '') return id.trim();
    } catch {
      /* ignore */
    }
  }
  const token = localStorage.getItem('token');
  const payload = decodeJwtPayload(token || '');
  const cid = payload?.company_id;
  if (typeof cid === 'string' && cid.trim() !== '') return cid.trim();
  return '';
}
